import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth, API } from '../context/AuthContext';
import AppHeader from '../components/AppHeader';
import {
    Users, Lock, CheckCircle, ArrowRight, Plus, Trash, Upload,
    PaintBucket, FileCsv, Crown, CircleNotch, ArrowSquareOut,
} from '@phosphor-icons/react';

/**
 * Team admin dashboard for the Developer / Enterprise tier.
 *
 * Gated by /api/b2b/team — if the caller isn't on a team this page
 * redirects to /developer. If they're a team MEMBER (not admin) it
 * shows a read-only view: team name, fellow seats, branding preview.
 */
export default function Team() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search] = useSearchParams();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/b2b/team`, { withCredentials: true });
            setData(res.data);
        } catch (err) {
            toast.error('Could not load team — please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (user) load(); }, [user, load]);

    // Post-checkout success — handled by global CheckoutReturnHandler;
    // here we just kick a refresh once when we land on /team?checkout=success.
    useEffect(() => {
        if (search.get('checkout') === 'success') {
            // Give the backend a moment to process the webhook.
            const t = setTimeout(load, 2500);
            return () => clearTimeout(t);
        }
    }, [search, load]);

    if (!user) return null;
    if (loading) return (
        <div className="min-h-screen flex items-center justify-center">
            <CircleNotch size={28} weight="bold" className="animate-spin" color="#1FA5CF" />
        </div>
    );
    if (!data || !data.team) {
        return (
            <div className="min-h-screen bg-white">
                <AppHeader title="Team workspace" subtitle="Developer tier" back />
                <main className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center" data-testid="team-empty-state">
                    <Lock size={36} weight="duotone" color="#52525B" className="mx-auto" />
                    <h1 className="font-display text-2xl font-bold mt-3">You're not on a team yet</h1>
                    <p className="mt-2 text-sm text-[#52525B] max-w-md mx-auto">
                        Developer-tier teams give housebuilders multi-seat access, branded PDFs and CSV imports.
                        Subscribe annually to spin one up.
                    </p>
                    <button
                        onClick={() => navigate('/developer')}
                        data-testid="team-empty-cta"
                        className="mt-6 inline-flex items-center gap-2 bg-[#1FA5CF] text-white px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150"
                    >
                        See Developer tier <ArrowRight size={14} weight="bold" />
                    </button>
                </main>
            </div>
        );
    }

    const { team, is_admin: isAdmin, seats, active } = data;
    const isEnterprise = team.tier === 'enterprise';
    const tierBadgeColor = isEnterprise ? '#FBBF24' : '#7C3AED';

    return (
        <div className="min-h-screen bg-white pb-24">
            <AppHeader
                title={team.name}
                subtitle={`${isEnterprise ? 'Enterprise' : 'Developer'} tier · ${team.seat_used}/${team.seat_cap} seats used`}
                back
            />

            <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-8" data-testid="team-page">
                {/* Status strip */}
                <section className="border border-[#18181B] p-4 flex flex-wrap items-center gap-4" data-testid="team-status">
                    <span
                        className="inline-flex items-center gap-1.5 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-white"
                        style={{ background: tierBadgeColor }}
                    >
                        <Crown size={12} weight="fill" /> {isEnterprise ? 'Enterprise' : 'Developer'}
                    </span>
                    {active ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-[#00875A] font-mono uppercase tracking-[0.18em]">
                            <CheckCircle size={14} weight="fill" /> Active until {new Date(team.active_until).toLocaleDateString('en-GB')}
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs text-[#E53935] font-mono uppercase tracking-[0.18em]">
                            <Lock size={14} weight="fill" /> Subscription inactive
                        </span>
                    )}
                    <span className="text-xs text-[#52525B] ml-auto">
                        Stripe status: <code className="bg-[#F4F4F5] px-1">{team.stripe_status || 'manual'}</code>
                    </span>
                    {isAdmin && team.stripe_customer_id && (
                        <CancelButton />
                    )}
                </section>

                {/* Seats */}
                <Section title="Seats" subtitle={`${seats.length} / ${team.seat_cap}`}>
                    {isAdmin && active && (
                        <InvitePanel onInvited={load} seatRemaining={team.seat_cap - team.seat_used} />
                    )}
                    <SeatList seats={seats} isAdmin={isAdmin} onRevoke={load} />
                </Section>

                {isAdmin && active && (
                    <>
                        <Section title="Branding" subtitle="Goes on every PDF and shared report">
                            <BrandingPanel team={team} onChange={load} />
                        </Section>

                        <Section title="Bulk import" subtitle="CSV of plots → DefectR properties">
                            <CsvImportPanel />
                        </Section>
                    </>
                )}
            </main>
        </div>
    );
}

function Section({ title, subtitle, children }) {
    return (
        <section>
            <div className="flex items-baseline justify-between mb-3">
                <h2 className="font-display text-xl font-semibold">{title}</h2>
                {subtitle && <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#52525B]">{subtitle}</span>}
            </div>
            {children}
        </section>
    );
}

function InvitePanel({ onInvited, seatRemaining }) {
    const [emailsRaw, setEmailsRaw] = useState('');
    const [sending, setSending] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const emails = emailsRaw
            .split(/[\s,;]+/)
            .map(s => s.trim().toLowerCase())
            .filter(s => /\S+@\S+\.\S+/.test(s));
        if (emails.length === 0) {
            toast.error('Enter at least one valid email.');
            return;
        }
        if (emails.length > seatRemaining) {
            toast.error(`Only ${seatRemaining} seat(s) left.`);
            return;
        }
        setSending(true);
        try {
            const res = await axios.post(`${API}/b2b/team/invite`, { emails }, { withCredentials: true });
            toast.success(`Invited ${res.data.invited.length} seat(s).`);
            setEmailsRaw('');
            onInvited?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Invite failed.');
        } finally {
            setSending(false);
        }
    };

    return (
        <form onSubmit={submit} className="border border-[#E4E4E7] p-4 mb-4 bg-[#FAFAFA]" data-testid="invite-panel">
            <label htmlFor="invite-emails" className="label-tiny block mb-1.5">Invite by email</label>
            <textarea
                id="invite-emails"
                data-testid="invite-emails-input"
                rows={2}
                value={emailsRaw}
                onChange={(e) => setEmailsRaw(e.target.value)}
                placeholder="alice@bigbuilder.com, bob@bigbuilder.com"
                className="w-full border border-[#18181B] outline-none px-3 py-2 text-sm font-mono"
            />
            <div className="flex items-center gap-3 mt-2">
                <button
                    type="submit"
                    disabled={sending}
                    data-testid="send-invites-btn"
                    className="inline-flex items-center gap-2 bg-[#1FA5CF] text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150 disabled:opacity-60"
                >
                    {sending ? <CircleNotch size={12} weight="bold" className="animate-spin" /> : <Plus size={12} weight="bold" />}
                    Send invites
                </button>
                <span className="text-xs text-[#52525B]">{seatRemaining} seat(s) remaining · invites expire in 14 days</span>
            </div>
        </form>
    );
}

function SeatList({ seats, isAdmin, onRevoke }) {
    const revoke = async (email) => {
        const t = toast(`Revoke ${email}?`, {
            description: 'This immediately removes the seat. The user can be re-invited later.',
            duration: 8000,
            action: {
                label: 'Revoke',
                onClick: async () => {
                    try {
                        await axios.post(`${API}/b2b/team/revoke`, { email }, { withCredentials: true });
                        toast.success('Seat revoked.');
                        onRevoke?.();
                    } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Could not revoke.');
                    }
                },
            },
            cancel: { label: 'Cancel', onClick: () => toast.dismiss(t) },
        });
    };

    if (seats.length === 0) {
        return <div className="border border-[#E4E4E7] p-4 text-sm text-[#52525B]">No seats yet.</div>;
    }
    return (
        <ul className="divide-y divide-[#E4E4E7] border border-[#E4E4E7]" data-testid="seat-list">
            {seats.map(s => (
                <li key={s.email} className="p-3 flex items-center gap-3" data-testid={`seat-row-${s.email}`}>
                    <Users size={18} weight="duotone" color="#1FA5CF" />
                    <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold truncate">
                            {s.email}
                            {s.is_admin && (
                                <span className="ml-2 inline-block bg-[#7C3AED] text-white font-mono text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5">
                                    Admin
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-[#52525B]">
                            <StatusPill status={s.status} />
                            {' · '}
                            {s.properties || 0} propert{(s.properties || 0) === 1 ? 'y' : 'ies'} · {s.snags || 0} defect{(s.snags || 0) === 1 ? '' : 's'}
                            {s.last_active && ` · Active ${new Date(s.last_active).toLocaleDateString('en-GB')}`}
                        </div>
                    </div>
                    {isAdmin && !s.is_admin && s.status !== 'revoked' && (
                        <button
                            onClick={() => revoke(s.email)}
                            data-testid={`revoke-${s.email}`}
                            className="text-xs text-[#52525B] hover:text-[#E53935] inline-flex items-center gap-1"
                        >
                            <Trash size={12} /> Revoke
                        </button>
                    )}
                </li>
            ))}
        </ul>
    );
}

function StatusPill({ status }) {
    const map = {
        active:  { bg: '#00875A', label: 'Active' },
        invited: { bg: '#F59E0B', label: 'Invited' },
        revoked: { bg: '#A1A1AA', label: 'Revoked' },
    };
    const c = map[status] || map.active;
    return (
        <span
            className="inline-block px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-white"
            style={{ background: c.bg }}
        >{c.label}</span>
    );
}

function BrandingPanel({ team, onChange }) {
    const [color, setColor] = useState(team?.branding?.header_color || '#1FA5CF');
    const [saving, setSaving] = useState(false);

    const onLogo = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        setSaving(true);
        const fd = new FormData();
        fd.append('logo', f);
        try {
            await axios.post(`${API}/b2b/team/branding`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Logo uploaded.');
            onChange?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Upload failed.');
        } finally {
            setSaving(false);
        }
    };

    const saveColor = async () => {
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('header_color', color);
            await axios.post(`${API}/b2b/team/branding`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Brand colour updated.');
            onChange?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="border border-[#E4E4E7] p-4 grid md:grid-cols-2 gap-4" data-testid="branding-panel">
            <div>
                <div className="label-tiny mb-2">Logo</div>
                <label className="block border border-dashed border-[#A1A1AA] p-5 text-center cursor-pointer hover:bg-[#F4F4F5]">
                    <Upload size={20} weight="duotone" color="#1FA5CF" className="mx-auto" />
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#52525B]">
                        {team?.branding?.logo_path ? 'Replace logo' : 'Upload logo (PNG/JPG/SVG · 2MB max)'}
                    </div>
                    <input data-testid="branding-logo-input" type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={onLogo} />
                </label>
            </div>
            <div>
                <div className="label-tiny mb-2">Brand colour</div>
                <div className="flex items-center gap-2">
                    <input
                        type="color"
                        value={color}
                        onChange={(e) => setColor(e.target.value)}
                        data-testid="branding-color-input"
                        className="w-12 h-10 border border-[#18181B] cursor-pointer"
                    />
                    <input
                        type="text"
                        value={color}
                        onChange={(e) => setColor(e.target.value)}
                        className="flex-1 border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-2 font-mono text-sm"
                    />
                    <button
                        onClick={saveColor}
                        disabled={saving}
                        data-testid="branding-save-color"
                        className="inline-flex items-center gap-1 bg-[#09090B] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-[#27272A] disabled:opacity-60"
                    >
                        <PaintBucket size={12} weight="bold" /> Save
                    </button>
                </div>
                <p className="mt-2 text-[10px] text-[#52525B]">
                    Used as the header stripe on PDF reports and the team-branded shared portal.
                </p>
            </div>
        </div>
    );
}

function CsvImportPanel() {
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        setImporting(true);
        setResult(null);
        const fd = new FormData();
        fd.append('file', f);
        try {
            const res = await axios.post(`${API}/b2b/team/properties/import`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setResult(res.data);
            toast.success(`Imported ${res.data.total} propert${res.data.total === 1 ? 'y' : 'ies'}.`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Import failed.');
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="border border-[#E4E4E7] p-4" data-testid="csv-import-panel">
            <label className="block border border-dashed border-[#A1A1AA] p-6 text-center cursor-pointer hover:bg-[#F4F4F5]">
                <FileCsv size={24} weight="duotone" color="#1FA5CF" className="mx-auto" />
                <div className="mt-2 font-display font-semibold">
                    {importing ? 'Importing…' : 'Drop a CSV here'}
                </div>
                <div className="mt-1 text-xs text-[#52525B]">
                    Columns: <code className="bg-[#F4F4F5] px-1">plot_number</code>, <code className="bg-[#F4F4F5] px-1">address</code>
                </div>
                <input data-testid="csv-input" type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} disabled={importing} />
            </label>
            {result && (
                <div className="mt-3 text-xs">
                    <div className="text-[#00875A]">✓ {result.total} created</div>
                    {result.skipped?.length > 0 && (
                        <div className="text-[#E53935] mt-1">
                            ✗ {result.skipped.length} skipped — {result.skipped.slice(0, 3).map(s => `row ${s.row}: ${s.reason}`).join('; ')}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function CancelButton() {
    const [busy, setBusy] = useState(false);
    const open = async () => {
        setBusy(true);
        try {
            const res = await axios.post(`${API}/b2b/team/cancel`, {}, { withCredentials: true });
            if (res.data?.url) {
                window.location.href = res.data.url;
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not open Stripe portal.');
        } finally {
            setBusy(false);
        }
    };
    return (
        <button
            onClick={open}
            disabled={busy}
            data-testid="cancel-subscription-btn"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#18181B] font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-[#F4F4F5] disabled:opacity-60"
        >
            Manage billing <ArrowSquareOut size={12} weight="bold" />
        </button>
    );
}
